'use client'

import Image from 'next/image'
import { Building2 } from 'lucide-react'
import { useAuth } from '@/providers/AuthContext'
import { useProfilePicture } from '@/hooks/useProfilePicture'
import { firstName, greetingFor } from '../_lib/greeting'

interface WelcomeHeaderProps {
  channelCount: number
  unreadTotal: number
  /** Recebida por parâmetro pra manter o componente testável sem mockar o relógio. */
  now: Date
}

/**
 * Cabeçalho da home: identidade de QUEM (empresa) e PRA QUEM (operador).
 *
 * Tudo aqui degrada em silêncio. `tenant_name` não existe em back anterior a
 * 08e4957a e é `null` em tenant sem profile; o nome do operador só chega no
 * `/auth/me` (o bootstrap do login o deixa vazio). Nenhum dos dois pode virar
 * buraco de layout nem imprimir "null" na tela.
 */
export function WelcomeHeader({ channelCount, unreadTotal, now }: WelcomeHeaderProps) {
  const { user } = useAuth()
  const { src, isError } = useProfilePicture()

  const company = user?.tenant_name?.trim()
  const operator = firstName(user?.name ?? '')
  const greeting = greetingFor(now)

  const channelPart = `${channelCount} ${channelCount === 1 ? 'canal' : 'canais'}`
  const unreadPart =
    unreadTotal > 0
      ? `${unreadTotal} ${unreadTotal === 1 ? 'mensagem' : 'mensagens'} aguardando`
      : null

  return (
    <header className="flex flex-col gap-7">
      <div className="flex items-center gap-3">
        {/* Mesma regra do rail: só usa a imagem com src E sem erro. */}
        {src && !isError ? (
          <Image
            src={src}
            alt=""
            width={40}
            height={40}
            unoptimized
            className="size-10 rounded-xl border border-border object-cover"
          />
        ) : (
          <div className="flex size-10 items-center justify-center rounded-xl border border-border bg-muted text-muted-foreground">
            <Building2 className="size-5" />
          </div>
        )}
        {company && (
          <span className="truncate text-sm font-semibold tracking-tight text-foreground/90">
            {company}
          </span>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <h1 className="text-3xl font-semibold tracking-tight text-foreground">
          {operator ? `${greeting}, ${operator}` : greeting}
        </h1>
        {/* A carga de trabalho é a tese da tela: é o que decide pra onde o
            operador vai. Antes era mais um cinza do mesmo peso que o resto, e
            por isso não decidia nada. */}
        <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
          <span className="text-muted-foreground">{channelPart}</span>
          {unreadPart && (
            <>
              <span aria-hidden="true" className="text-muted-foreground/40">
                ·
              </span>
              <span className="font-semibold tabular-nums text-brand-accent">{unreadPart}</span>
            </>
          )}
        </p>
      </div>
    </header>
  )
}
